import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { NotificationModule } from '@progress/kendo-angular-notification';
import DataTable from 'datatables.net-dt';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { EdrCsvExportService } from '../../services/edr-csv-export.service';

/** Column definition for an EDR drill-down grid. */
export interface EdrGridColumn {
  field: string;
  title: string;
  /** Defaults to true. Set false for non-filterable columns (e.g. timestamps). */
  filterable?: boolean;
  /**
   * Cell rendering hint: 'date' formats via DatePipe, 'boolean' maps
   * true/false to Yes/No. Omit for plain text.
   */
  type?: 'date' | 'boolean';
}

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

/**
 * Reusable drill-down grid dialog for EDR HUD widgets.
 */
@Component({
  selector: 'app-edr-grid-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DialogsModule, ButtonModule, NotificationModule, DatatableComponent],
  templateUrl: './edr-grid-dialog.component.html',
})
export class EdrGridDialogComponent {
  private csvExport = inject(EdrCsvExportService);

  private locale = inject(LOCALE_ID);

  public visible = input<boolean>(false);
  /** Fully-resolved OData endpoint (organization id already embedded). */
  public url = input<string>('');
  public title = input<string>('View Data');
  public columns = input<EdrGridColumn[]>([]);
  /** Report type key used to queue the CSV export, e.g. 'edr-coverage-report'. */
  public reportKey = input<string>('');
  public organizationId = input<number | null>(null);
  /** [start, end] date range from the EDR tab period selector. */
  public dateRange = input<string[] | null>(null);

  public visibleChange = output<boolean>();

  /** True while a CSV export is being queued. */
  public isExporting = signal<boolean>(false);


  public datatableColumns = computed(() =>
    this.columns().map((col) => ({
      data: col.field,
      name: col.field,
      title: col.title,
      type: 'string',
      render:
        col.type === 'date'
          ? (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '')
          : col.type === 'boolean'
            ? (value: unknown) => (value ? 'Yes' : 'No')
            : text,
    }))
  );

  exportCsv(): void {
    this.isExporting.set(true);
    this.csvExport
      .export({
        reportKey: this.reportKey(),
        organizationId: this.organizationId(),
        dateRange: this.dateRange(),
      })
      .subscribe({
        next: () => this.isExporting.set(false),
        error: () => this.isExporting.set(false),
      });
  }

  close(): void {
    this.visibleChange.emit(false);
  }
}
